import { isNil } from "lodash";

import {
  PolicyCustomizationForm,
  ValidationKey,
  mapValidationKeyToAccessor,
} from "@covertree/new-business-forms/src/validatorsAndTypes/policyCustomization";
import {
  GetPricedCustomizedPolicyQueryVariables,
  UnitLevelCoveragesInput,
} from "@covertree/new-business-graphql";
import {
  mapNumberToDollarsEnum,
  mapNumberToGolfCartValueEnum,
  mapStringToPercentEnum,
  mapStringToSettlementOption,
} from "@covertree/new-business-mappings";

export const mapFormStateToCustomizationPayload = (
  formValues: PolicyCustomizationForm
): Omit<GetPricedCustomizedPolicyQueryVariables, "policyLocator"> => {
  return {
    effectiveDate: formValues.effectiveDate,
    policyLevelCoverages: Object.entries(
      formValues.policyLevelCoverages
    ).reduce(
      (acc, [validationKey, value]) => {
        const [primaryKey, secondaryKey] = validationKey.split("_") as [
          keyof GetPricedCustomizedPolicyQueryVariables["policyLevelCoverages"],
          string,
        ];
        return {
          ...acc,
          [primaryKey]: {
            ...acc[primaryKey],
            [secondaryKey]: value,
          },
        };
      },
      {} as GetPricedCustomizedPolicyQueryVariables["policyLevelCoverages"]
    ),
    unitLevelCoverages: {
      exposureLocator: formValues.units[0].exposureLocator,
      coverages: mapPrimaryValuesToGraphQL(
        formValues.units[0].unitLevelCoverages
      ),
    },
  };
};

export const mapPrimaryValuesToGraphQL = (
  unitLevelCoverages: PolicyCustomizationForm["units"][0]["unitLevelCoverages"]
): GetPricedCustomizedPolicyQueryVariables["unitLevelCoverages"]["coverages"] =>
  Object.entries(unitLevelCoverages).reduce(
    (acc, [validationKey, value]) => {
      const [primaryKey, secondaryKey] = (validationKey as ValidationKey).split(
        "_"
      ) as [keyof UnitLevelCoveragesInput, string];

      if (
        isNil(value) ||
        Array.isArray(value) ||
        ["coverageA_value", "coverageB_value", "coverageC_value"].includes(
          validationKey
        )
      )
        return {
          ...acc,
          [primaryKey]: {
            ...acc[primaryKey],
            [secondaryKey]: value,
          },
        };

      if (secondaryKey === "settlementOption") {
        return {
          ...acc,
          [primaryKey]: {
            ...acc[primaryKey],
            [secondaryKey]: mapStringToSettlementOption(value as string),
          },
        };
      }

      if (typeof value === "string" && primaryKey !== "animalLiability") {
        return {
          ...acc,
          [primaryKey]: {
            ...acc[primaryKey],
            [secondaryKey]: mapStringToPercentEnum(
              value,
              mapValidationKeyToAccessor(validationKey)
            ),
          },
        };
      }

      if (primaryKey === "golfCarts") {
        return {
          ...acc,
          [primaryKey]: {
            ...acc[primaryKey],
            [secondaryKey]: mapNumberToGolfCartValueEnum(value as number),
          },
        };
      }

      if (typeof value === "number" || primaryKey === "animalLiability") {
        return {
          ...acc,
          [primaryKey]: {
            ...acc[primaryKey],
            [secondaryKey]: mapNumberToDollarsEnum(
              value as string | number,
              mapValidationKeyToAccessor(validationKey)
            ),
          },
        };
      }

      return {
        ...acc,
        [primaryKey]: {
          ...acc[primaryKey],
          [secondaryKey]: value,
        },
      };
    },
    {} as GetPricedCustomizedPolicyQueryVariables["unitLevelCoverages"]["coverages"]
  );
